import React, { useState } from 'react';
import { Link2 } from 'lucide-react';

/**
 * "Use a link instead of a copy", for any kind of media.
 *
 * This is the shell only — a field, a button, and the one line of status the
 * attempt earned. What a link *means* differs by medium (a picture may need a
 * page looked up; a clip has to be shown to play), so the caller supplies the
 * resolver and the words. See ImageLinkInput and AudioLinkInput, which are both
 * thin wrappers over this: the control was duplicated once and the copies drift,
 * so there is one of it.
 */
// The settings panels are light; the launch screen's design options are dark.
// Same control either way — only the paint changes.
export const TONES = {
  light: {
    label: 'text-gray-700',
    // Not gray-500. On a plain white panel that reads 4.8:1, but the app's
    // light-mode sheet repaints gray-500 *lighter* (#94a3b8), which drops this
    // blurb to 2.5:1 — and the blurb is the longest thing the control says.
    // gray-600 is not in that sheet, so it is the same dark grey either way.
    description: 'text-gray-600',
    field: 'border-gray-300 bg-white text-gray-800 disabled:bg-gray-100',
    button: 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50',
    ok: 'text-emerald-700',
    error: 'text-red-600',
    warning: 'text-amber-700'
  },
  dark: {
    label: 'text-gray-200',
    // Not the light tone's grey. #6b7280 reads at 4.8:1 on the white panel
    // this control started on and 3.0:1 on a dark one, which is under AA for
    // text this size — the blurb is the longest thing the control says.
    description: 'text-gray-400',
    field: 'border-gray-600 bg-gray-900 text-gray-100 placeholder-gray-500 disabled:bg-gray-800',
    button: 'border-gray-600 bg-gray-700/50 text-gray-200 hover:border-gray-400 hover:bg-gray-700',
    ok: 'text-emerald-400',
    error: 'text-red-400',
    warning: 'text-amber-400'
  }
};

const MediaLinkInput = ({
  /** (raw) => Promise<{ok: true, ...}|{ok: false, error}>. Never throws. */
  resolve,
  /** (result) => void, called only for a successful resolve. */
  onResolved,
  /** (result) => string — the success line. */
  describeSuccess,
  /** (result) => string — an optional caveat under it. Empty for none. */
  describeWarning,
  label,
  description,
  placeholder,
  buttonLabel = 'Use link',
  checkingLabel = 'Checking…',
  tone = 'light',
  disabled = false
}) => {
  const palette = TONES[tone] || TONES.light;
  const [value, setValue] = useState('');
  const [status, setStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  const handleUse = async () => {
    const raw = value.trim();
    if (!raw || isChecking) return;

    setIsChecking(true);
    setStatus(null);
    const result = await resolve(raw);
    setIsChecking(false);

    if (!result?.ok) {
      setStatus({ tone: 'error', message: result?.error || 'That link could not be used.' });
      return;
    }

    onResolved?.(result);
    setValue('');
    setStatus({
      tone: 'ok',
      message: describeSuccess ? describeSuccess(result) : 'Linked. The deck stores the address, not the file.',
      warning: describeWarning ? describeWarning(result) : ''
    });
  };

  return (
    <div className="space-y-2">
      <div>
        <p className={`text-sm font-semibold ${palette.label}`}>{label}</p>
        {description && <p className={`text-xs mt-0.5 ${palette.description}`}>{description}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="url"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleUse();
            }
          }}
          placeholder={placeholder}
          aria-label={label}
          disabled={disabled}
          className={`min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 ${palette.field}`}
        />
        <button
          type="button"
          onClick={handleUse}
          disabled={disabled || isChecking || !value.trim()}
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition disabled:opacity-60 ${palette.button}`}
        >
          <Link2 size={16} />
          {isChecking ? checkingLabel : buttonLabel}
        </button>
      </div>
      {status && (
        <>
          <p
            className={`text-xs font-semibold ${status.tone === 'error' ? palette.error : palette.ok}`}
            role={status.tone === 'error' ? 'alert' : 'status'}
          >
            {status.message}
          </p>
          {status.warning && (
            <p className={`text-xs ${palette.warning}`} role="status">{status.warning}</p>
          )}
        </>
      )}
    </div>
  );
};

export default MediaLinkInput;
